import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Auth0Provider } from './contexts/Auth0Context';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ImpersonationBanner from './components/ImpersonationBanner';
import Login from './pages/Login';
import LoginSecure from './pages/LoginSecure';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminDashboardV2 from './pages/SuperAdminDashboardV2';
import ClientDashboard from './pages/ClientDashboard';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import CreateCampaign from './pages/CreateCampaign';
import Templates from './pages/Templates';
import TemplateLibrary from './pages/TemplateLibrary';
import Contacts from './pages/Contacts';
import Analytics from './pages/Analytics';
import AnalyticsDashboardV2 from './pages/AnalyticsDashboardV2';
import Settings from './pages/Settings';
import Onboarding from './pages/Onboarding';
import CostSimulator from './pages/CostSimulator';
import RCSAgents from './pages/RCSAgents';
import SendRCS from './pages/SendRCS';
import BillingDashboard from './pages/BillingDashboard';
import RCSEventsLive from './pages/RCSEventsLive';
import Auth0Callback from './pages/Auth0Callback';
import APIKeys from './pages/APIKeys';
import APIDeveloper from './pages/APIDeveloper';
import RCSTesters from './pages/RCSTesters';
import Blacklist from './pages/Blacklist';
import Archives from './pages/Archives';
import Documentation from './pages/Documentation';
import SMSMailing from './pages/SMSMailing';
import SMSMailingNew from './pages/SMSMailingNew';
import SMSQueue from './pages/SMSQueue';
import SMSArchives from './pages/SMSArchives';
import SMSContacts from './pages/SMSContacts';
import SMSBlacklist from './pages/SMSBlacklist';
import SMSStatistics from './pages/SMSStatistics';
import SMSTemplates from './pages/SMSTemplates';
import DataImport, { DataImportCentral } from './pages/DataImport';
import Logs from './pages/Logs';
import DocShareAccess from './pages/DocShareAccess';
import DocShareView from './pages/DocShareView';
// Nouveaux modules fusionnés
import RCSCampaign from './pages/RCSCampaign';
import EventsAnalytics from './pages/EventsAnalytics';
import TemplatesHub from './pages/TemplatesHub';
import APIHub from './pages/APIHub';
import OnboardingHub from './pages/OnboardingHub';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const redirectPath = user.role === 'superadmin' ? '/superadmin' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginSecure />} />
      <Route path="/login/legacy" element={<Login />} />
      <Route path="/reset-password" element={<LoginSecure />} />
      <Route path="/auth0/callback" element={<Auth0Callback />} />
      
      {/* Public doc share access (no auth required) */}
      <Route path="/doc-share/:token" element={<DocShareAccess />} />
      <Route path="/doc-share-view" element={<DocShareView />} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {user?.role === 'superadmin' ? <Navigate to="/superadmin" replace /> : <Navigate to="/dashboard" replace />}
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/superadmin"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin-advanced"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SuperAdminDashboardV2 />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Campaigns />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/campaigns/new"
        element={
          <ProtectedRoute>
            <CreateCampaign />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <CampaignDetail />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <Templates />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/template-library"
        element={
          <ProtectedRoute>
            <TemplateLibrary />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/data-management"
        element={
          <ProtectedRoute>
            <DataImportCentral />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/analytics/v2"
        element={
          <ProtectedRoute>
            <AnalyticsDashboardV2 />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/blacklist"
        element={
          <ProtectedRoute>
            <Blacklist />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/archives"
        element={
          <ProtectedRoute>
            <Archives />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/cost-simulator"
        element={
          <ProtectedRoute>
            <CostSimulator />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/rcs-agents"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <RCSAgents />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/send-rcs"
        element={
          <ProtectedRoute>
            <SendRCS />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <BillingDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/rcs-events"
        element={
          <ProtectedRoute>
            <RCSEventsLive />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
            <APIKeys />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/api-developer"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
            <APIDeveloper />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/rcs-testers"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
            <RCSTesters />
          </ProtectedRoute>
        }
      />
      
      {/* Nouveaux modules fusionnés */}
      <Route
        path="/rcs-campaign"
        element={
          <ProtectedRoute>
            <RCSCampaign />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/events-analytics"
        element={
          <ProtectedRoute>
            <EventsAnalytics />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/templates-hub"
        element={
          <ProtectedRoute>
            <TemplatesHub />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/api-hub"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
            <APIHub />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/onboarding-hub"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <OnboardingHub />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/documentation"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <Documentation />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/logs"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <Logs />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms-mailing"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSMailing />
          </ProtectedRoute>
        }
      />
      
      {/* SMS Module Routes */}
      <Route
        path="/sms/mailing"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSMailingNew />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/queue"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSQueue />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/archives"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSArchives />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/contacts"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSContacts />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/data-import"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <DataImport />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/blacklist"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSBlacklist />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/statistics"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSStatistics />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/templates"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <SMSTemplates />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sms/settings"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Auth0Provider>
        <AuthProvider>
          <BrowserRouter>
            <ImpersonationBanner />
            <AppRoutes />
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </Auth0Provider>
    </ThemeProvider>
  );
}

export default App;